import type { FacetRef, ProductCard } from '@sbg/shared/schemas';

/** `[20]` → `{ min: 20, max: 20 }` · `[18, 30, 20]` → `{ min: 18, max: 30 }` · `[]` → `null`. */
export function thicknessRange(values: readonly number[]): { min: number; max: number } | null {
  if (values.length === 0) return null;
  return { min: Math.min(...values), max: Math.max(...values) };
}

export interface ProductFact {
  key: 'color' | 'finish' | 'origin' | 'thickness';
  label: string;
  /** Colour swatch (`#rrggbb`) for the colour chip. */
  swatch?: string;
}

const HEX = /^#[0-9a-f]{3}(?:[0-9a-f]{3})?$/i;

const facetLabel = (facet: FacetRef): string => facet.label;

/**
 * The short details every card shows — colour · finish · origin · thickness — in that order, each
 * only when the product has it. Several finishes collapse to the first plus a count ("Polished +1");
 * several thicknesses to a range ("18–30").
 */
export function productFacts(
  product: Pick<ProductCard, 'color' | 'finishes' | 'origin' | 'thicknessOptionsMm'>,
  thickness: (range: { min: number; max: number }) => string,
  formatCount: (count: number) => string = String,
): ProductFact[] {
  const facts: ProductFact[] = [];
  if (product.color) {
    facts.push({
      key: 'color',
      label: facetLabel(product.color),
      ...(product.color.swatchHex && HEX.test(product.color.swatchHex)
        ? { swatch: product.color.swatchHex }
        : {}),
    });
  }
  const [firstFinish, ...moreFinishes] = product.finishes;
  if (firstFinish) {
    const extra = moreFinishes.length > 0 ? ` +${formatCount(moreFinishes.length)}` : '';
    facts.push({ key: 'finish', label: `${facetLabel(firstFinish)}${extra}` });
  }
  if (product.origin) facts.push({ key: 'origin', label: facetLabel(product.origin) });
  const range = thicknessRange(product.thicknessOptionsMm);
  if (range) facts.push({ key: 'thickness', label: thickness(range) });
  return facts;
}
